import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetAssignmentByClusterQuery,
  useGetAllAssignmentQuestionQuery,
  useGetCampusQuery,
  useGetClustersByCampusQuery,
  useGetStaffByEmailIdQuery,
  useGetStudentsResultsQuery,
} from "../../../../graphql/generated.tsx";
import ReactSelect from "react-select";
import AdminLayout from "../../Layout/index.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import LightSpinner from "../../../../components/LightSpinner";
import Pagination from "../../../../components/Pagination/index.jsx";
import Tour from "reactour";
import { LuMousePointerClick } from "react-icons/lu";
import { AiOutlineClockCircle, AiOutlineQuestionCircle } from "react-icons/ai";

const staffFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
    "&:hover": {
      borderColor: "#9ca3af",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    paddingRight: 2,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 6,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#d1d5db"
      : state.isFocused
      ? "#f3f4f6"
      : "#ffffff",
    color: "#111827",
    ":active": {
      backgroundColor: "#d1d5db",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
  }),
};

const ACCENTS = [
  { bar: "#8A5A2B", pillBg: "#F3E8DA", pillText: "#8A5A2B", pillBgHover: "#EAD9C3" },
  { bar: "#0F6E56", pillBg: "#E1F5EE", pillText: "#0F6E56", pillBgHover: "#C9EADF" },
  { bar: "#3C3489", pillBg: "#EEEDFE", pillText: "#3C3489", pillBgHover: "#E0DFFE" },
];

const getRowAccent = (globalIndex, rowSize = 3) => {
  const rowIndex = Math.floor(globalIndex / rowSize);
  const indexInRow = globalIndex % rowSize;
  const permutations = [
    [0, 1, 2],
    [1, 2, 0],
    [2, 0, 1],
    [0, 2, 1],
    [2, 1, 0],
    [1, 0, 2],
  ];
  const order = permutations[rowIndex % permutations.length];
  return ACCENTS[order[indexInRow] ?? 0];
};

const CalSVG = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 1.5v2M8.5 1.5v2M1.5 5.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ArrowSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #e5e7eb",
  boxSizing: "border-box",
  fontFamily: "Poppins, sans-serif",
};

const AssignmentCard = ({ assignment, globalIndex, selectedClusterId, onNavigate, dataTour }) => {
  const accent = getRowAccent(globalIndex, 3);
  const mildTopBar = `${accent.bar}99`;
  const [isHovered, setIsHovered] = useState(false);
  const { data: assignmentQuestionsData } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId: assignment?.id || "", page: 1, limit: 1000 },
    skip: !assignment?.id,
  });

  const questionsCount = assignmentQuestionsData?.getAllAssignmentQuestion?.length ?? null;
  const formatDurationLabel = (durationValue) => {
    if (durationValue === null || durationValue === undefined || durationValue === "") {
      return "-";
    }

    const raw = String(durationValue).trim();
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return "-";

    // Support HH.MM style only when exactly 2 minute digits (e.g., 1.30 => 1 hr 30 min).
    const [hoursPart, minutesPartRaw] = raw.split(".");
    if (minutesPartRaw !== undefined) {
      const parsedHours = Number.parseInt(hoursPart, 10) || 0;
      const parsedMinutes = Number.parseInt(minutesPartRaw, 10);

      if (
        minutesPartRaw.length === 2 &&
        Number.isFinite(parsedMinutes) &&
        parsedMinutes >= 0 &&
        parsedMinutes <= 59
      ) {
        if (parsedMinutes === 0) return `${parsedHours} hr`;
        return `${parsedHours} hr ${parsedMinutes} min`;
      }
    }

    // Fallback: treat value as decimal hours (e.g., 1.5 => 1 hr 30 min).
    const totalMinutes = Math.round(numeric * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (mins === 0) return `${hrs} hr`;
    return `${hrs} hr ${mins} min`;
  };

  const durationLabel = formatDurationLabel(assignment?.duration);

  const dynamicCardStyle = {
    ...cardStyle,
    transform: "translateY(0) scale(1)",
    boxShadow: "0 12px 30px -18px rgba(15, 23, 42, 0.2)",
    transition: "box-shadow 0.22s ease",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  };

  const arrowContainerStyle = {
    width: 26, height: 26, borderRadius: "50%",
    background: isHovered ? accent.pillBgHover : "#F9FAFB",
    border: `1px solid ${isHovered ? accent.bar : "#EAECF0"}`,
    color: isHovered ? accent.bar : "#667085",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    transform: isHovered ? "scale(1.12)" : "scale(1)",
    boxShadow: isHovered ? `0 6px 14px -8px ${accent.bar}` : "none",
    transition: "background 0.2s ease-in-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
  };

  return (
    <div
      data-tour={dataTour}
      onClick={() => onNavigate(`/assignment/${assignment.id}?clusterId=${selectedClusterId}`)}
      style={dynamicCardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Coloured top bar */}
      <div style={{ height: 7, background: mildTopBar, width: "100%", flexShrink: 0 }} />

      {/* Body */}
      <div style={{ padding: "20px 18px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <p style={{
          margin: 0, fontSize: 16, fontWeight: 600, color: "#101828",
          lineHeight: 1.45,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {assignment.name}
        </p>
        </div>

        {/* Description */}
        <p style={{
          margin: 0, fontSize: 14, color: "#5B6475", lineHeight: 1.7, flex: 1, fontWeight: 500,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {assignment.short_description || "Take this assessment based on what you know and demonstrate your understanding."}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "4px 0", marginTop: 6 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
              <AiOutlineClockCircle size={16} color="#64748B" />
              {durationLabel}
            </div>
            <div style={{ width: 1, height: 16, background: "#E2E8F0" }} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
              <AiOutlineQuestionCircle size={16} color="#64748B" />
              {questionsCount ?? "-"} questions
            </div>
            <div style={{ width: 1, height: 16, background: "#E2E8F0" }} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
              <CalSVG />
              {assignment.due_date
                ? new Date(assignment.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                : "No due date"}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: "12px 18px 16px",
        display: "flex", alignItems: "center",
        borderTop: "1px solid #F2F4F7",
        gap: 10,
        background: "linear-gradient(90deg, rgba(248,250,252,0.9), rgba(255,255,255,0.6))",
      }}>
        {assignment.domain ? (
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "3px 9px",
            borderRadius: 8,
            letterSpacing: "0.03em",
            whiteSpace: "nowrap",
            background: accent.pillBg,
            color: accent.pillText,
          }}>
            {assignment.domain.toUpperCase()}
          </span>
        ) : (
          <span />
        )}
        <div style={{ ...arrowContainerStyle, marginLeft: "auto" }}>
          <ArrowSVG />
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
const StaffAssignmentReport = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const helperMessage = "Click on any assignment card to view submissions and scores.";

  const { data: staffData, loading: staffLoading } = useGetStaffByEmailIdQuery({ variables: { email } });

  const { data: campusDetails, loading: campusLoading } = useGetCampusQuery({
    variables: { id: staffData?.getStaffByEmailId?.campusId },
    skip: staffLoading,
  });

  const campus = campusDetails?.campus;

  const { data: clusters, loading: clusterLoading } = useGetClustersByCampusQuery({
    variables: { campusName: campus?.name, limit: 200 },
    skip: campusLoading,
  });

  const allClusters = clusters?.getClustersByCampus || [];

  const availableClusters = staffData?.getStaffByEmailId?.isAdminStaff
    ? allClusters
    : allClusters.filter((c) => staffData?.getStaffByEmailId?.clusterIds?.includes(c.id));

  const sortedClusters = [...availableClusters].sort((a, b) => a.name.localeCompare(b.name));
  const clusterOptions = sortedClusters.map((cluster) => ({ value: cluster.id, label: cluster.name }));
  const dateFilterOptions = [
    { value: "", label: "Till Date" },
    { value: "15", label: "Last 15 Days" },
    { value: "30", label: "Last 30 Days" },
    { value: "90", label: "Last 3 Months" },
    { value: "180", label: "Last 6 Months" },
    { value: "custom", label: "Custom Range" },
  ];

  const [selectedClusterId, setSelectedClusterId] = useState(() => searchParams.get("clusterId") || "");
  const [currentPage, setCurrentPage] = useState(() => {
    const page = Number(searchParams.get("page") || 1);
    return Number.isFinite(page) && page > 0 ? page : 1;
  });
  const CARDS_PER_PAGE = 9;

  const { data: assignmentData, loading: assignmentLoading, refetch: refetchAssignments } =
    useGetAssignmentByClusterQuery({
      variables: { campusName: campus?.name, clusterId: selectedClusterId },
      skip: clusterLoading || !selectedClusterId,
    });

  const { loading, refetch } = useGetStudentsResultsQuery({
    variables: {
      campusId: campus?.id, page: 1, limit: 200,
      clusterid: selectedClusterId || availableClusters?.[0]?.id,
    },
    skip: clusterLoading,
  });

  const [filterType, setFilterType] = useState(() => {
    const filterParam = searchParams.get("filter");
    if (filterParam === "all") return "";
    return filterParam ?? "15";
  });
  const [customFrom, setCustomFrom] = useState(() => searchParams.get("from") || "");
  const [customTo, setCustomTo] = useState(() => searchParams.get("to") || "");
  const [searchText, setSearchText] = useState(() => searchParams.get("q") || "");
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [typedHelperText, setTypedHelperText] = useState("");
  const [shouldStartTyping, setShouldStartTyping] = useState(false);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    if (permissions?.showStaffReportTour && !loading && !assignmentLoading) {
      setTimeout(() => setIsTourOpen(true), 800);
    }
  }, [loading, assignmentLoading]);

  useEffect(() => {
    if (
      loading ||
      assignmentLoading ||
      staffLoading ||
      campusLoading ||
      clusterLoading
    )
      return;
    setShouldStartTyping(true);
  }, [loading, assignmentLoading, staffLoading, campusLoading, clusterLoading]);

  useEffect(() => {
    if (!shouldStartTyping) return;

    setTypedHelperText("");
    let currentIndex = 0;

    const typingTimer = setInterval(() => {
      currentIndex += 1;
      setTypedHelperText(helperMessage.slice(0, currentIndex));

      if (currentIndex >= helperMessage.length) {
        clearInterval(typingTimer);
      }
    }, 40);

    return () => clearInterval(typingTimer);
  }, [shouldStartTyping, helperMessage]);

  const tourSteps = [
    { selector: '[data-tour="cluster-select"]', content: "Select different batches to view their respective assignments and reports." },
    { selector: '[data-tour="search-input"]', content: "Search by assignment name or domain to quickly find specific assignments." },
    { selector: '[data-tour="filter-dropdown"]', content: "Apply date filters to view assignments from different time periods." },
    { selector: '[data-tour="assignment-card"]', content: "Click on any assignment card to view detailed reports including student submissions, scores, and performance analytics." },
  ];

  const closeTour = (disableAll = false) => {
    setIsTourOpen(false);
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    if (permissions) {
      if (disableAll) {
        permissions.showStudentLookupTour = false;
        permissions.showStaffReportTour = false;
        permissions.showAllStudentsTour = false;
        permissions.showStaffDashboardTabsTour = false;
      } else {
        permissions.showStaffReportTour = false;
      }
      localStorage.setItem("tourPermissions", JSON.stringify(permissions));
    }
  };

  useEffect(() => {
    if (sortedClusters?.length > 0) {
      const exists = sortedClusters.some((c) => c.id === selectedClusterId);
      if (!exists) setSelectedClusterId(sortedClusters[0].id);
    }
  }, [sortedClusters]);

  useEffect(() => {
    if (selectedClusterId) {
      refetch({ campusId: campus?.id, page: 1, limit: 50, clusterid: selectedClusterId }).catch(() => {});
      refetchAssignments?.({ campusName: campus?.name, clusterId: selectedClusterId }).catch(() => {});
    }
    setCurrentPage(1);
  }, [selectedClusterId]);

  const assignments = assignmentData?.getAssignmentByCluster || [];

  const filteredAssignments = useMemo(() => {
    let list = assignments;
    if (filterType) {
      const now = new Date();
      let startDate;
      switch (filterType) {
        case "15": startDate = new Date(); startDate.setDate(now.getDate() - 15); break;
        case "30": startDate = new Date(); startDate.setDate(now.getDate() - 30); break;
        case "90": startDate = new Date(); startDate.setMonth(now.getMonth() - 3); break;
        case "180": startDate = new Date(); startDate.setMonth(now.getMonth() - 6); break;
        case "custom":
          if (customFrom && customTo) {
            const from = new Date(customFrom);
            const to = new Date(customTo);
            list = list.filter((a) => { const d = new Date(a.due_date); return d >= from && d <= to; });
          }
          break;
        default: break;
      }
      if (filterType !== "custom" && startDate) {
        list = list.filter((a) => new Date(a.due_date) >= startDate);
      }
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter((a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.short_description || "").toLowerCase().includes(q) ||
        (a.domain || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [filterType, customFrom, customTo, searchText, assignments]);

  const totalPages = Math.max(1, Math.ceil(filteredAssignments.length / CARDS_PER_PAGE));

  const paginatedAssignments = useMemo(() => {
    const start = (currentPage - 1) * CARDS_PER_PAGE;
    return filteredAssignments.slice(start, start + CARDS_PER_PAGE);
  }, [currentPage, filteredAssignments]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [selectedClusterId, filterType, customFrom, customTo, searchText]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    ["clusterId", "filter", "from", "to", "q", "page"].forEach((k) => nextParams.delete(k));

    if (selectedClusterId) nextParams.set("clusterId", selectedClusterId);
    if (filterType === "") {
      nextParams.set("filter", "all");
    } else if (filterType) {
      nextParams.set("filter", filterType);
    }
    if (customFrom) nextParams.set("from", customFrom);
    if (customTo) nextParams.set("to", customTo);
    if (searchText.trim()) nextParams.set("q", searchText.trim());
    if (currentPage > 1) nextParams.set("page", String(currentPage));

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [selectedClusterId, filterType, customFrom, customTo, searchText, currentPage, searchParams, setSearchParams]);

  if (loading || assignmentLoading || staffLoading || campusLoading || clusterLoading) {
    return (
      <AdminLayout>
        <div className="max-w-full p-8">
          <div className="flex flex-col items-center justify-center h-screen gap-2">
            <LightSpinner message="Loading Assignments" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Tour
        steps={tourSteps}
        isOpen={isTourOpen}
        onRequestClose={(target) => {
          const isX = target?.type === "click" && target?.target?.closest('[data-tour-elem="close"]');
          closeTour(isX);
        }}
        accentColor="#2563eb"
        rounded={12}
        showNumber={false}
        showNavigation={true}
        showButtons={true}
        showCloseButton={true}
        lastStepNextButton={<button style={{ backgroundColor: "#05385d", color: "white", padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "14px", fontWeight: "500" }}>Got it!</button>}
        nextButton={<button style={{ backgroundColor: "#05385d", color: "white", padding: "8px 20px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "14px", fontWeight: "500" }}>Next →</button>}
        prevButton={<button style={{ backgroundColor: "transparent", color: "#6b7280", padding: "8px 20px", borderRadius: "8px", border: "1px solid #d1d5db", cursor: "pointer", fontSize: "14px", fontWeight: "500" }}>← Back</button>}
        closeWithMask={false}
        disableInteraction={true}
      />

      <div className="max-w-full font-poppins">
        <div className="max-w-full px-6 pt-3 pb-3 bg-white border border-b-0 border-gray-200 shadow-md sm:px-6 rounded-t-xl">
          <div className="relative w-full sm:w-[760px] max-w-full overflow-hidden rounded-xl border border-sky-300 bg-sky-100 px-5 py-4 shadow-sm min-h-[96px]">
            <div className="absolute inset-y-0 left-0 w-1 bg-sky-900" />
            <div className="flex items-center gap-2.5 pl-2">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 bg-white border border-sky-200 rounded-full shadow-sm">
                <LuMousePointerClick className="text-sky-900" size={18} />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-tight text-slate-800 sm:text-lg">
                  View Assignment Report
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-700 sm:text-base">
                  {typedHelperText}
                  {typedHelperText.length < helperMessage.length && (
                    <span className="inline-block w-0.5 h-4 ml-0.5 bg-slate-700 animate-pulse"></span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="max-w-full p-4 mb-0 bg-white border border-t-0 border-b-0 border-gray-200 shadow-md sm:p-6">
          <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-end sm:gap-4">
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:gap-3">
              <div data-tour="cluster-select" className="w-full sm:w-48">
                <p className="mb-1 text-sm font-semibold text-gray-600">Batch details</p>
                <ReactSelect
                  isSearchable={false}
                  isDisabled={sortedClusters?.length === 1}
                  options={clusterOptions}
                  value={clusterOptions.find((option) => option.value === selectedClusterId) || null}
                  onChange={(option) => setSelectedClusterId(option?.value || "")}
                  styles={staffFilterSelectStyles}
                />
              </div>
              <div data-tour="filter-dropdown" className="w-full sm:w-48">
                <p className="mb-1 text-sm font-semibold text-gray-600">Date filter</p>
                <ReactSelect
                  isSearchable={false}
                  options={dateFilterOptions}
                  value={dateFilterOptions.find((option) => option.value === filterType) || dateFilterOptions[0]}
                  onChange={(option) => setFilterType(option?.value ?? "")}
                  styles={staffFilterSelectStyles}
                />
              </div>
              {filterType === "custom" && (
                <>
                  <div className="w-full sm:w-auto">
                    <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                  </div>
                  <div className="w-full sm:w-auto">
                    <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                  </div>
                </>
              )}
            </div>
            <div data-tour="search-input" className="relative w-full sm:w-72 flex-shrink-0">
              <input
                type="text"
                placeholder="Search by name or domain"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchText && (
                <button onClick={() => setSearchText("")} className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600">✕</button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-full min-h-screen p-4 pb-10 bg-white border border-t-0 border-gray-200 shadow-md sm:p-6 sm:pb-12 rounded-b-xl">
          {filteredAssignments.length === 0 ? (
            <div style={{ padding: "56px 0", textAlign: "center", color: "#667085", fontSize: 14 }}>
              No assignments found.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2 lg:grid-cols-3">
                {paginatedAssignments.map((assignment, index) => {
                  const globalIndex = (currentPage - 1) * CARDS_PER_PAGE + index;
                  return (
                    <AssignmentCard
                      key={assignment.id}
                      assignment={assignment}
                      globalIndex={globalIndex}
                      selectedClusterId={selectedClusterId}
                      onNavigate={navigate}
                      dataTour={index === 0 ? "assignment-card" : ""}
                    />
                  );
                })}
              </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </div>

        {/* Help button */}
        <button
          onClick={() => setIsTourOpen(true)}
          style={{ backgroundColor: "#053859" }}
          className="absolute z-10 flex items-center justify-center w-10 h-10 text-white rounded-full shadow-lg sm:w-8 sm:h-8 top-3 right-3 sm:top-4 sm:right-4 focus:ring-4 focus:ring-blue-300"
          aria-label="Help"
        >
          <span className="text-base font-bold sm:text-lg">?</span>
        </button>
      </div>
    </AdminLayout>
  );
};

export default StaffAssignmentReport;
